import React, { HTMLAttributes, PropsWithChildren, ReactNode } from 'react'
import clsx from 'clsx'
import { Header as HeaderStyles } from '@airbus/elements/elements'
import { Menu as MenuIcon } from '@mui/icons-material'
import { createUseStyles, ColorModeProvider, useTheme } from '../../lib/styling'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import useMediaQuery from '../../lib/hooks/useMediaQuery'
import { HeaderThemeMode } from '../helperTypes'
import IconButton from '../IconButton'
import Tabs from '../Tabs'
import { CommonProps } from '../helperTypes'

// TODO reimplement prominent/static/mobile props later ?

type Props = PropsWithChildren<{
  /** Application name */
  appName?: string | ReactNode
  /** Callback fired when the Logo or AppName is clicked */
  onHomeClick?: () => void
  /** Callback fired when the Menu button is clicked */
  onMenuButtonClick?: () => void
  /** Show Menu icon button */
  showMenuButton?: boolean
  /**
   * Theme/color that will impact children variants
   * @default "dark"
   */
  theme?: HeaderThemeMode
}>

export type HeaderProps = Props & CommonProps

const useStyles = createUseStyles(HeaderStyles, 'header')

const Header = ({
  appName,
  className,
  children,
  onHomeClick,
  onMenuButtonClick,
  showMenuButton,
  theme = 'dark',
  ...rest
}: HeaderProps) => {
  const {
    responsive: { breakpoint },
  } = useTheme()
  const mqMaxSM = useMediaQuery(`(max-width: ${breakpoint.sm}px)`)

  return (
    <ColorModeProvider mode={theme === 'light' ? 'default' : 'dark'}>
      <HeaderWrapper className={className} showMenuButton={showMenuButton} theme={theme} {...rest}>
        <HeaderHome appName={appName} onHomeClick={onHomeClick} />

        {React.Children.map(children, (child) => {
          if (!React.isValidElement(child)) return null
          if (child.type !== Tabs) return child

          // TABS props
          return React.cloneElement<any>(child, {
            ...(child.props as any),
            floating: true,
            scrollable: mqMaxSM,
            'aria-label': 'navigation',
          })
        })}

        {showMenuButton && (
          <IconButton variant='ghost' onClick={onMenuButtonClick} aria-label='Open the menu'>
            <MenuIcon />
          </IconButton>
        )}
      </HeaderWrapper>
    </ColorModeProvider>
  )
}
export default Header

type HeaderWrapperProps = Pick<HeaderProps, 'children' | 'showMenuButton' | 'theme'> & HTMLAttributes<HTMLDivElement>

// separated component to ensure useStyles gets
// correct theme context value from ColorModeProvider
const HeaderWrapper = ({ children, className, showMenuButton, theme, ...rest }: HeaderWrapperProps) => {
  const classes = useStyles()
  return (
    <div
      className={clsx(className, classes.root, classes[`_${theme}`], showMenuButton && classes._nopadding)}
      {...rest}
    >
      {children}
    </div>
  )
}

type HeaderHomeProps = Pick<HeaderProps, 'appName' | 'onHomeClick'>

const HeaderHome = ({ appName, onHomeClick }: HeaderHomeProps) => {
  const classes = useStyles()
  const hasHomeClick = onHomeClick !== undefined
  const LogoAppname = () => (
    <>
      <div className={classes.logo} />
      <h1 className={classes.appname}>{appName}</h1>
    </>
  )
  if (hasHomeClick) {
    return (
      <div onClick={onHomeClick} className={classes.home}>
        <LogoAppname />
      </div>
    )
  }
  return <LogoAppname />
}

// retrocompat : Deprecated "AppBar" component
export const AppBar = (props: HeaderProps) => {
  useDeprecatedComponent('AppBar', 'Please use Header.')
  return <Header {...props} />
}

// retrocompat : Deprecated "AppTitle" component
export const AppTitle = (props: PropsWithChildren<{}>) => {
  useDeprecatedComponent('AppTitle', 'Please use Header `appName` prop.')
  return <h1>{props.children}</h1>
}
